/**
 * Team Modal Component
 * Manage team members with supervisor API persistence
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@/features/auth/hooks/useSession';
import { useWorkspacePlanTier } from '@/features/billing/hooks/useWorkspacePlanTier';
import { useIsCompactLayout } from '@/common/hooks';
import { apiGet, apiPost, apiDelete, ApiError } from '@/lib/api/client';
import { apiFetch } from '@/lib/apiClient';
import type { SeatUsage } from '@/lib/api/endpoints/spaces';
import { describeAuditEntry } from '@/common/utils/audit-labels';
// Note: /auth/* and /workspaces/* base URLs are resolved inside the api client
// helpers — components no longer template env.apiBaseUrl / env.authBaseUrl directly.
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import type { TeamMember, PendingInvite, ContainerAccess, AllInvite } from '@/types/session';
import { PermissionDropdown } from './team/PermissionDropdown';
import { RemoveConfirmModal } from './team/RemoveConfirmModal';
import { BlockConfirmModal } from './team/BlockConfirmModal';
import { sortInvites, roleToPermission, permissionToRole, permissionLabel } from './team/helpers';
import type { TeamModalProps } from './team/types';
import './TeamModal.css';

// Re-export TeamMember type for backwards compatibility
export type { TeamMember } from '@/types/session';

export function TeamModal({ isOpen, onClose, initialTab = 'my-team' }: TeamModalProps) {
  // Close on escape key
  useEscapeKey(onClose, isOpen);
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);
  const isMobile = useIsCompactLayout();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    token,
    profile,
    selectedContainer,
    containers,
    pendingInvites,
    acceptInvite,
    rejectInvite,
    ignoreInvite,
    leaveWorkspace,
    fetchContainers,
    selectContainer,
    refresh
  } = useSession();
  const workspacePlanTier = useWorkspacePlanTier();

  const [activeTab, setActiveTab] = useState<'my-team' | 'shared-workspaces' | 'settings' | 'activity'>(initialTab);

  const [activityLog, setActivityLog] = useState<Array<{ id: string; action: string; actor_id: string; target_type: string | null; target_id: string | null; details: Record<string, unknown> | null; created_at: number }>>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityHasMore, setActivityHasMore] = useState(false);

  // Sync tab when initialTab changes (e.g., opening from Invitations menu)
  useEffect(() => { if (isOpen) setActiveTab(initialTab); }, [isOpen, initialTab]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processingInvite, setProcessingInvite] = useState<string | null>(null);
  // For name prompt when accepting invite
  const [pendingAcceptInvite, setPendingAcceptInvite] = useState<string | null>(null);
  const [acceptName, setAcceptName] = useState('');
  // For owner setup/edit
  const [ownerName, setOwnerName] = useState('');
  const [spaceName, setSpaceName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  // Invitations tab state
  const [allInvites, setAllInvites] = useState<AllInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  // Resend invite status per invite id
  const [resendStatus, setResendStatus] = useState<Record<string, string>>({});

  // Inline email editing state
  const [editingInviteEmail, setEditingInviteEmail] = useState<{ inviteId: string; email: string } | null>(null);

  // Confirmation modal state
  const [removeConfirmation, setRemoveConfirmation] = useState<{ memberEmail: string; email: string } | null>(null);
  const [blockConfirmation, setBlockConfirmation] = useState<{ inviteId: string; ownerEmail: string } | null>(null);

  const [removeModalRef, handleRemoveOverlayClick, handleRemoveOverlayMouseDown] =
    useModalOverlayClick(() => setRemoveConfirmation(null), 30);
  const [blockModalRef, handleBlockOverlayClick, handleBlockOverlayMouseDown] =
    useModalOverlayClick(() => setBlockConfirmation(null), 30);

  useEscapeKey(() => setRemoveConfirmation(null), removeConfirmation !== null);
  useEscapeKey(() => setBlockConfirmation(null), blockConfirmation !== null);


  // Check if current user is the owner of the selected container. #3447: default
  // to NOT-owner when there is no selected container (zero-workspace / team-seat-
  // only state) — never treat a workspace-less user as an owner.
  const isOwner = selectedContainer?.isOwner ?? false;

  // ── Seats (team owners): show usage + add/remove seats via the prorated
  //    POST /payment/update-seats (Teams S7 / #3627). Hidden for non-team /
  //    non-owner / no-subscription workspaces (the seat-usage fetch just no-ops).
  const [seatUsage, setSeatUsage] = useState<SeatUsage | null>(null);
  const [seatTarget, setSeatTarget] = useState<number | null>(null);
  const [seatBusy, setSeatBusy] = useState(false);
  const [seatMsg, setSeatMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !isOwner || selectedContainer?.type !== 'team' || !selectedContainer?.id) {
      setSeatUsage(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await apiGet<SeatUsage>(`/payment/seat-usage?workspaceId=${selectedContainer.id}`);
        if (!cancelled) { setSeatUsage(data); setSeatTarget(data.seatCap); }
      } catch { /* no subscription / not team — leave the control hidden */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isOwner, selectedContainer?.type, selectedContainer?.id]);

  const handleUpdateSeats = async () => {
    if (!selectedContainer?.id || seatTarget == null || seatBusy) return;
    setSeatBusy(true);
    setSeatMsg(null);
    try {
      const { data } = await apiPost<{ updated: boolean } & SeatUsage>('/payment/update-seats', {
        workspaceId: selectedContainer.id,
        newSeatCount: seatTarget,
      });
      setSeatUsage({
        memberCount: data.memberCount,
        pendingInvites: data.pendingInvites,
        used: data.used,
        seatCap: data.seatCap,
        available: data.available,
      });
      setSeatTarget(data.seatCap);
      setSeatMsg(`Updated to ${data.seatCap} seats.`);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.payload as { error?: string } | undefined)?.error : null;
      setSeatMsg(msg || 'Could not update seats. Please try again.');
    } finally {
      setSeatBusy(false);
    }
  };

  // Check if owner has set up their profile (name and space name)
  const ownerProfileComplete = Boolean(profile?.name && profile?.containerName);

  // Initialize owner name/space name from profile when modal opens
  useEffect(() => {
    if (isOpen && profile) {
      setOwnerName(profile.name || '');
      setSpaceName(profile.containerName || '');
    }
  }, [isOpen, profile]);

  // Load team members from supervisor API on mount
  useEffect(() => {
    if (isOpen && token) {
      loadTeamMembers();
      fetchContainers(); // Also refresh invites
    }
  }, [isOpen, token]);

  const loadTeamMembers = async () => {
    if (!token || !selectedContainer?.id) return;
    setLoading(true);
    try {
      const { data } = await apiGet<{ members?: any[] }>(`/workspaces/${selectedContainer.id}/members`);
      // The API returns membership `role` + status 'active'|'pending'; the UI
      // model (TeamMember) speaks `permission` + status 'accepted'|'pending'.
      const members: TeamMember[] = (data.members ?? []).map((m: any) => ({
        id: m.id,
        email: m.email,
        permission: roleToPermission(m.role),
        status: m.status === 'pending' ? 'pending' : 'accepted',
        invitedAt: m.joinedAt,
      }));
      setTeamMembers(members);
    } catch (err) {
      console.error('Failed to load team members', err);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Reject malformed emails + the most common .com typos ("test2@test.cmo" — the
  // QA case). Pragmatic shape check + a small typo blocklist, not a full PSL.
  const isValidInviteEmail = (email: string): boolean => {
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email)) return false;
    const tld = email.slice(email.lastIndexOf('.') + 1).toLowerCase();
    return !['cmo', 'con', 'vom', 'comm', 'xom', 'ocm', 'cm', 'om', 'co m'].includes(tld);
  };

  const handleAddMember = async () => {
    const trimmedEmail = newEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Please enter an email address');
      return;
    }

    if (!isValidInviteEmail(trimmedEmail)) {
      setError('Please enter a valid email address (e.g. name@example.com)');
      return;
    }

    if (teamMembers.some(m => m.email === trimmedEmail)) {
      setError('This email is already in your team');
      return;
    }

    if (!token) {
      setError('Please sign in to add team members');
      return;
    }

    setSaving(true);
    try {
      await apiPost(`/workspaces/${selectedContainer?.id}/invites`, {
        email: trimmedEmail,
        role: 'member',
      });

      // Re-fetch so the new invitee is normalized into the TeamMember shape
      // (status 'pending', mapped permission) rather than the raw invite row.
      await loadTeamMembers();
      setNewEmail('');
      setError(null);
    } catch (err: any) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError('This user is already a team member or has blocked invites');
          return;
        }
        if (err.status === 400) {
          const errData = err.payload as { error?: string } | undefined;
          setError(errData?.error || 'Invalid request');
          return;
        }
      }
      console.error('Failed to invite team member', err);
      setError('Failed to invite team member');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePermission = async (memberId: string, memberEmail: string, permission: 'read' | 'read_write' | 'admin') => {
    if (!token || !selectedContainer?.id) return;
    try {
      // Send the canonical role (admin/member/read_only); `admin` is only settable via `role`.
      const role = permissionToRole(permission);
      // PATCH carries no ETag here, so it stays on apiFetch (explicit token) rather
      // than the ETag-required apiPatch helper.
      await apiFetch(`workspaces/${selectedContainer.id}/members/${memberId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
        authToken: token,
      });

      setTeamMembers(teamMembers.map(m =>
        m.email === memberEmail ? { ...m, permission } : m
      ));
    } catch (err) {
      console.error('Failed to update permission', err);
    }
  };

  const handleRemoveMember = (memberEmail: string) => {
    const member = teamMembers.find(m => m.email === memberEmail);
    if (!member) return;
    setRemoveConfirmation({ memberEmail, email: member.email });
  };

  const executeRemoveMember = async () => {
    if (!removeConfirmation || !token || !selectedContainer?.id) return;
    const { memberEmail } = removeConfirmation;
    setRemoveConfirmation(null);
    const member = teamMembers.find(m => m.email === memberEmail);
    if (!member) return;
    try {
      await apiDelete(`/workspaces/${selectedContainer.id}/members/${member.id}`);
      setTeamMembers(teamMembers.filter(m => m.email !== memberEmail));
    } catch (err) {
      console.error('Failed to remove team member', err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddMember();
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    if (!token) return;
    try {
      setResendStatus(prev => ({ ...prev, [inviteId]: 'sending' }));
      // #3305: invite resend is consolidated onto the OpenAPI /workspaces/* surface.
      await apiPost(`/workspaces/invites/${inviteId}/resend`, {});
      setResendStatus(prev => ({ ...prev, [inviteId]: 'sent' }));
      setTimeout(() => setResendStatus(prev => ({ ...prev, [inviteId]: '' })), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setResendStatus(prev => ({ ...prev, [inviteId]: 'limit' }));
        return;
      }
      console.error('Failed to resend invite:', err);
      setResendStatus(prev => ({ ...prev, [inviteId]: 'error' }));
    }
  };

  const handleChangeInviteEmail = async (inviteId: string, newEmail: string) => {
    if (!token) return;
    try {
      // #3305: changing an invite's email is consolidated onto the OpenAPI /workspaces/*
      // surface — now PATCH /workspaces/invites/:id. PATCH carries no ETag, so it stays
      // on apiFetch (explicit token) rather than the ETag-required apiPatch helper.
      await apiFetch(`workspaces/invites/${inviteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ email: newEmail }),
        authToken: token,
      });
      setEditingInviteEmail(null);
      await loadTeamMembers();
    } catch (err) {
      console.error('Failed to change invite email:', err);
    }
  };

  const handleAcceptInviteClick = (inviteId: string) => {
    // Show name prompt before accepting
    setPendingAcceptInvite(inviteId);
    setAcceptName('');
  };

  const handleConfirmAccept = async () => {
    if (!pendingAcceptInvite) return;
    const trimmedName = acceptName.trim();
    if (!trimmedName) {
      setError('Please enter your name');
      return;
    }

    setProcessingInvite(pendingAcceptInvite);
    setError(null);
    try {
      // First update the user's name via the API. apiFetch auto-routes /auth/* to
      // the supervisor (authBaseUrl) and carries refresh-token rotation. A failed
      // profile update must NOT block accepting the invite (matches prior behavior,
      // which ignored the PATCH response), so swallow its error.
      try {
        await apiFetch('/auth/profile', {
          method: 'PATCH',
          body: JSON.stringify({ user_name: trimmedName }),
          authToken: token ?? undefined,
        });
      } catch (profileErr) {
        console.error('Failed to update name before accepting invite', profileErr);
      }

      // Then accept the invite
      await acceptInvite(pendingAcceptInvite);
      setPendingAcceptInvite(null);
      setAcceptName('');
    } catch (err) {
      console.error('Failed to accept invite', err);
      setError('Failed to accept invite');
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleCancelAccept = () => {
    setPendingAcceptInvite(null);
    setAcceptName('');
    setError(null);
  };

  const handleRejectInviteClick = (inviteId: string, ownerEmail: string) => {
    setBlockConfirmation({ inviteId, ownerEmail });
  };

  const executeRejectInvite = async () => {
    if (!blockConfirmation) return;
    const { inviteId } = blockConfirmation;
    setBlockConfirmation(null);
    setProcessingInvite(inviteId);
    try {
      await rejectInvite(inviteId);
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleSelectContainer = async (containerId: string) => {
    await selectContainer(containerId);
  };

  const handleSaveProfile = async () => {
    const trimmedName = ownerName.trim();
    const trimmedSpace = spaceName.trim();

    if (!trimmedName) {
      setError('Please enter your name');
      return;
    }
    if (!trimmedSpace) {
      setError('Please enter a workspace name');
      return;
    }

    if (!token) return;

    setSavingProfile(true);
    setError(null);
    try {
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          user_name: trimmedName,
          container_name: trimmedSpace,
        }),
        authToken: token,
      });

      // Refresh session + container list so sidebar/workspace labels update immediately.
      await refresh();
      await fetchContainers();
      setEditingProfile(false);
    } catch (err) {
      console.error('Failed to save profile', err);
      setError('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Settings tab: save workspace name only
  const [settingsSpaceName, setSettingsSpaceName] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Sync the settings input with the latest container name. Re-runs when
  // the underlying data changes — important after handleSaveProfile or
  // handleSaveWorkspaceName triggers refresh()/fetchContainers(): without
  // this dep, the input could keep showing the pre-save value.
  useEffect(() => {
    if (isOpen) {
      setSettingsSpaceName(selectedContainer?.containerName || profile?.containerName || '');
      setSettingsSaved(false);
    }
  }, [isOpen, selectedContainer?.containerName, profile?.containerName]);

  const handleSaveWorkspaceName = async () => {
    const trimmed = settingsSpaceName.trim();
    if (!trimmed) {
      setError('Please enter a workspace name');
      return;
    }
    if (!token) return;

    setSavingSettings(true);
    setError(null);
    try {
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          container_name: trimmed,
        }),
        authToken: token,
      });

      await refresh();
      await fetchContainers();
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save workspace name', err);
      setError('Failed to save workspace name');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleEditProfile = () => {
    setOwnerName(profile?.name || '');
    setSpaceName(profile?.containerName || '');
    setEditingProfile(true);
    setError(null);
  };

  const handleCancelEditProfile = () => {
    setOwnerName(profile?.name || '');
    setSpaceName(profile?.containerName || '');
    setEditingProfile(false);
    setError(null);
  };

  // Fetch all invitations
  const fetchAllInvites = useCallback(async () => {
    if (!token) return;

    setInvitesLoading(true);
    try {
      // #3305: GET /workspaces/invites/all returns camelCase keys per the contract
      // (xavior-openapi.yaml + spaces.ts handler) — already AllInvite-shaped, but
      // re-project field-by-field to drop any extra keys (matches prior behavior).
      const { data } = await apiGet<{ invites?: AllInvite[] }>('/workspaces/invites/all');
      const invites: AllInvite[] = (data.invites || []).map((inv) => ({
        id: inv.id,
        ownerId: inv.ownerId,
        ownerEmail: inv.ownerEmail,
        ownerName: inv.ownerName,
        containerName: inv.containerName,
        permission: inv.permission,
        status: inv.status,
        invitedAt: inv.invitedAt,
        acceptedAt: inv.acceptedAt,
        ignoredAt: inv.ignoredAt,
      }));
      setAllInvites(invites);
    } catch (err) {
      console.error('Failed to fetch invites', err);
    } finally {
      setInvitesLoading(false);
    }
  }, [token]);

  // Load invitations when Other Workspaces tab is opened
  useEffect(() => {
    if (isOpen && activeTab === 'shared-workspaces') {
      fetchAllInvites();
    }
  }, [isOpen, activeTab, fetchAllInvites]);

  const ACTIVITY_PAGE_SIZE = 50;

  const fetchActivity = useCallback(async (before?: number) => {
    if (!token || !selectedContainer?.id) return;
    setActivityLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(ACTIVITY_PAGE_SIZE) });
      if (before) params.set('before', String(before));
      const { data } = await apiGet<{ entries?: typeof activityLog }>(
        `/workspaces/${selectedContainer.id}/audit-log?${params}`,
      );
      const entries = data.entries ?? [];
      if (before) {
        setActivityLog(prev => [...prev, ...entries]);
      } else {
        setActivityLog(entries);
      }
      setActivityHasMore(entries.length >= ACTIVITY_PAGE_SIZE);
    } catch {
      if (!before) setActivityLog([]);
      setActivityHasMore(false);
    } finally {
      setActivityLoading(false);
    }
  }, [token, selectedContainer?.id]);

  useEffect(() => {
    if (isOpen && activeTab === 'activity') fetchActivity();
  }, [isOpen, activeTab, fetchActivity]);

  // Handle invite toggle (accept/ignore/leave)
  const handleInviteToggle = async (invite: AllInvite, accepted: boolean) => {
    try {
      if (accepted) {
        await acceptInvite(invite.id);
      } else {
        if (invite.status === 'accepted') {
          await leaveWorkspace(invite.ownerId);
        } else {
          await ignoreInvite(invite.id);
        }
      }
      await fetchAllInvites();
      await fetchContainers();
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  if (!isOpen) return null;

  const acceptedCount = teamMembers.filter(m => m.status === 'accepted').length;
  const pendingCount = teamMembers.filter(m => m.status === 'pending').length;
  const rejectedCount = teamMembers.filter(m => m.status === 'rejected').length;

  // Sort invites: pending first, then accepted, then ignored
  const sortedInvites = sortInvites(allInvites);

  const modalContent = (
    <div className={`team-modal-overlay ${isMobile ? 'mobile' : ''}`} onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <div ref={modalContentRef} className={`team-modal ${isMobile ? 'mobile' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="team-modal-header">
          <div>
            <h2>Workspace Settings</h2>
          </div>
          <button className="team-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="team-tabs">
          <button className={`tab-btn ${activeTab === 'my-team' ? 'active' : ''}`} data-text="My Workspace" onClick={() => setActiveTab('my-team')}>My Workspace</button>
          <button className={`tab-btn ${activeTab === 'shared-workspaces' ? 'active' : ''}`} data-text="Other Workspaces" onClick={() => setActiveTab('shared-workspaces')}>Other Workspaces</button>
          {selectedContainer?.isOwner !== false && (
            <button className={`tab-btn ${activeTab === 'activity' ? 'active' : ''}`} data-text="Activity" onClick={() => setActiveTab('activity')}>Activity</button>
          )}
        </div>

        <div className="team-tab-subtitle">
          {activeTab === 'my-team'
            ? 'Your workspace where you manage details, members, and access.'
            : activeTab === 'activity'
            ? 'Recent changes to workspace membership, roles, and settings.'
            : "Workspaces you're a member of that are managed by others."}
        </div>

        <div className="team-modal-body xv-scroll">
          {activeTab === 'my-team' ? (
            <>
          {/* Name Prompt Dialog - shown when accepting an invite */}
          {pendingAcceptInvite && (
            <div style={{ background: 'var(--color-surface-tinted, #F6F7F8)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 8 }}>Enter Your Name</div>
              <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: '0 0 12px' }}>So the workspace owner knows who you are:</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={acceptName}
                  onChange={(e) => setAcceptName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmAccept()}
                  placeholder="Your name"
                  autoFocus
                  style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 14, background: 'var(--color-surface)', color: 'var(--color-text)' }}
                />
                <button onClick={handleCancelAccept} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--color-text-muted)' }}>Cancel</button>
                <button onClick={handleConfirmAccept} disabled={!acceptName.trim() || processingInvite !== null} style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #3C4043)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, opacity: (!acceptName.trim() || processingInvite !== null) ? 0.5 : 1 }}>
                  {processingInvite ? 'Joining...' : 'Join'}
                </button>
              </div>
              {error && <p style={{ color: 'var(--color-danger, #EA4335)', fontSize: 12, marginTop: 8 }}>{error}</p>}
            </div>
          )}

          {/* My Workspace section */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faint)' }}>Details</div>
              <span
                aria-label={isOwner ? 'You own this workspace' : 'You are a member of this workspace'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  background: isOwner ? 'var(--color-primary, #3C4043)' : 'var(--color-surface-tinted, #ECECEF)',
                  color: isOwner ? '#fff' : 'var(--color-text-muted)',
                  border: isOwner ? 'none' : '1px solid var(--color-border-soft, #ECECEF)',
                }}
              >
                {isOwner ? 'Owner' : 'Member'}
              </span>
            </div>
            <div style={{ background: 'var(--color-surface-tinted, #F6F7F8)', border: '1px solid var(--color-border-soft, #ECECEF)', borderRadius: 12, padding: 16 }}>
              {isOwner ? (
                <>
                  {!ownerProfileComplete ? (
                    <>
                      <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: '0 0 12px' }}>Enter your name and workspace name to get started.</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Your name" style={{ padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 14, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
                        <input value={spaceName} onChange={(e) => setSpaceName(e.target.value)} placeholder="Workspace name" style={{ padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 14, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
                        <button onClick={handleSaveProfile} disabled={!ownerName.trim() || !spaceName.trim() || savingProfile} style={{ alignSelf: 'flex-start', padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #3C4043)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, opacity: (!ownerName.trim() || !spaceName.trim() || savingProfile) ? 0.5 : 1 }}>
                          {savingProfile ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                      {error && <p style={{ color: 'var(--color-danger, #EA4335)', fontSize: 12, marginTop: 8 }}>{error}</p>}
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--color-text-muted)' }}>
                        <span>Workspace Name:</span>
                        <input
                          id="settingsSpaceName"
                          type="text"
                          value={settingsSpaceName}
                          onChange={(e) => { setSettingsSpaceName(e.target.value); setSettingsSaved(false); setError(null); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                          onBlur={() => {
                            const trimmed = settingsSpaceName.trim();
                            const current = selectedContainer?.containerName || profile?.containerName || '';
                            if (trimmed && trimmed !== current) {
                              handleSaveWorkspaceName();
                            }
                          }}
                          placeholder="Workspace name"
                          style={{ flex: 1, padding: '4px 8px', border: '1px solid var(--color-border-soft, #ECECEF)', borderRadius: 6, fontSize: 14, fontWeight: 500, background: 'var(--color-surface)', color: 'var(--color-text-primary)', outline: 'none' }}
                        />
                      </div>
                      <div style={{ borderTop: '1px solid var(--color-border-soft, #ECECEF)', marginTop: 10, paddingTop: 10 }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                        <span style={{ color: 'var(--color-text-muted)' }}>Email:</span> <span style={{ color: 'var(--color-text-faint)', opacity: 0.7 }}>{profile?.email || ''}</span>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
                    Name: <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{selectedContainer?.containerName || 'Workspace'}</span>
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
                    Email: {selectedContainer?.ownerEmail || ''}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Seats — team owners (Teams S7 / #3627): add or remove seats, prorated */}
          {isOwner && selectedContainer?.type === 'team' && seatUsage && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faint)', marginBottom: 10 }}>Seats</div>
              <div style={{ background: 'var(--color-surface-tinted, #F6F7F8)', border: '1px solid var(--color-border-soft, #ECECEF)', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 12 }}>
                  <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{seatUsage.used} of {seatUsage.seatCap}</span> seats used{seatUsage.available > 0 ? ` · ${seatUsage.available} open` : ''}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>Seats:</span>
                  <button
                    type="button"
                    aria-label="Remove a seat"
                    onClick={() => setSeatTarget((n) => Math.max(Math.max(2, seatUsage.used), (n ?? seatUsage.seatCap) - 1))}
                    disabled={seatBusy || (seatTarget ?? seatUsage.seatCap) <= Math.max(2, seatUsage.used)}
                    style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'var(--color-text-muted)' }}
                  >−</button>
                  <span style={{ minWidth: 24, textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>{seatTarget ?? seatUsage.seatCap}</span>
                  <button
                    type="button"
                    aria-label="Add a seat"
                    onClick={() => setSeatTarget((n) => Math.min(50, (n ?? seatUsage.seatCap) + 1))}
                    disabled={seatBusy || (seatTarget ?? seatUsage.seatCap) >= 50}
                    style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'var(--color-text-muted)' }}
                  >+</button>
                  <button
                    type="button"
                    onClick={handleUpdateSeats}
                    disabled={seatBusy || seatTarget == null || seatTarget === seatUsage.seatCap}
                    style={{ marginLeft: 8, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #3C4043)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, opacity: (seatBusy || seatTarget == null || seatTarget === seatUsage.seatCap) ? 0.5 : 1 }}
                  >
                    {seatBusy ? 'Updating…' : 'Update seats'}
                  </button>
                </div>
                <p style={{ fontSize: 12, color: 'var(--color-text-faint)', margin: '10px 0 0' }}>
                  Changes are prorated and charged to your card immediately. Min {Math.max(2, seatUsage.used)}, max 50.
                </p>
                {seatMsg && (
                  <p style={{ fontSize: 12, color: seatMsg.startsWith('Updated') ? 'var(--color-success, #137333)' : 'var(--color-danger, #EA4335)', margin: '8px 0 0' }}>{seatMsg}</p>
                )}
              </div>
            </div>
          )}

          {/* Team Members section */}
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faint)', marginBottom: 10 }}>Team Members</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {loading ? (
              <p style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>Loading...</p>
            ) : (
              <>
                {/* Team members */}
                {teamMembers.filter(m => m.email !== profile?.email).map((member) => (
                  <div key={member.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--color-surface-tinted, #F6F7F8)', border: '1px solid var(--color-border-soft, #ECECEF)', opacity: member.status === 'rejected' ? 0.5 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--color-text-muted)' }}>
                          <span>Name: <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{member.name || member.email.split('@')[0]}</span></span>
                          {member.permission === 'admin' && (
                            <span
                              aria-label="Admin"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                background: 'var(--color-primary, #3C4043)',
                                color: '#fff',
                              }}
                            >
                              Admin
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 14, marginTop: 6 }}>
                          <span style={{ color: 'var(--color-text-muted)' }}>Email:</span> <span style={{ color: 'var(--color-text-faint)' }}>{member.email}</span>
                        </div>
                      </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {isOwner ? (
                        <PermissionDropdown
                          value={member.permission}
                          onChange={(v) => handleUpdatePermission(member.id, member.email, v)}
                          onRemove={() => handleRemoveMember(member.email)}
                          disabled={member.status === 'rejected'}
                        />
                      ) : (
                        <span style={{ fontSize: 14, color: 'var(--color-text-faint)' }}>{permissionLabel(member.permission)}</span>
                      )}
                    </div>
                    {/* Close flex row */}
                    </div>
                    {/* Invite Pending — full width below the flex row */}
                    {member.status === 'pending' && (
                      <>
                        <div style={{ borderTop: '1px solid var(--color-border-soft, #ECECEF)', marginTop: 10, paddingTop: 8 }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, color: '#8B6508' }}>Invite Pending</span>
                          {isOwner && (
                            <button
                              onClick={() => handleResendInvite(member.id)}
                              disabled={resendStatus[member.id] === 'sending'}
                              style={{ padding: '2px 8px', fontSize: 12, borderRadius: 5, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-muted)' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              {resendStatus[member.id] === 'sending' ? 'Sending...' : resendStatus[member.id] === 'sent' ? 'Sent!' : 'Resend'}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Empty state — only owner, no other members */}
                {teamMembers.filter(m => m.email !== profile?.email).length === 0 && !loading && workspacePlanTier === 'team' && (
                  <div style={{ textAlign: 'center', padding: '12px 0' }}>
                    <p style={{ fontSize: 14, color: 'var(--color-text-faint)', margin: 0 }}>No team members yet</p>
                    <p style={{ fontSize: 14, color: 'var(--color-text-faint)', margin: '4px 0 0', opacity: 0.7 }}>
                      {ownerProfileComplete
                        ? 'Invite someone below to get started'
                        : 'Set your name and workspace name above first, then invite teammates'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Team-plan owners get the invite form. Free / Pro owners get an
              upgrade-to-Team CTA — inviting people requires the Team plan. */}
          {isOwner && ownerProfileComplete && workspacePlanTier === 'team' && (
            <div style={{ marginTop: 16 }}>
              <div className="add-member-form">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setError(null); }}
                  onKeyDown={handleKeyDown}
                  placeholder="name@example.com"
                  className="add-member-input"
                  disabled={saving}
                />
                <button className="add-member-btn" onClick={handleAddMember} disabled={!newEmail.trim() || saving}>
                  {saving ? 'Sending...' : 'Invite'}
                </button>
              </div>
              {error && <p className="add-member-error">{error}</p>}
            </div>
          )}

          {/* Upgrade CTA: any owner not on the Team plan, regardless of
              profile completeness — the upgrade flow itself doesn't need a
              filled-in profile (PlanPicker collects what it needs). */}
          {isOwner && workspacePlanTier !== null && workspacePlanTier !== 'team' && (
            <div style={{
              marginTop: 16,
              padding: 20,
              borderRadius: 12,
              border: '1px solid var(--color-border)',
              background: 'rgba(14, 165, 233, 0.05)',
              textAlign: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
                Invite teammates with the Team plan
              </h3>
              <p style={{ margin: '6px 0 14px', fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                Share contacts, calendars, Pages and memory across your team.
              </p>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  const params = new URLSearchParams(location.search);
                  params.set('upgrade', '1');
                  navigate(`${location.pathname}?${params.toString()}`);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#0EA5E9',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#0284C7'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#0EA5E9'; }}
              >
                Upgrade to Team
              </button>
            </div>
          )}
            </>
          ) : activeTab === 'activity' ? (
            <div style={{ padding: '4px 0' }}>
              {activityLoading ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading activity...</p>
              ) : activityLog.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No activity recorded yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {activityLog.map(entry => {
                    const date = new Date(entry.created_at);
                    const timeStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' at ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                    const description = describeAuditEntry(entry.action, entry.details as Record<string, string> | null);
                    return (
                      <div key={entry.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--color-border-soft, #ECECEF)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                        <span style={{ fontSize: 13, color: 'var(--color-text)' }}>{description}</span>
                        <span style={{ fontSize: 12, color: 'var(--color-text-faint, #7a7f85)', whiteSpace: 'nowrap', flexShrink: 0 }}>{timeStr}</span>
                      </div>
                    );
                  })}
                  {activityHasMore && (
                    <button
                      onClick={() => {
                        const last = activityLog[activityLog.length - 1];
                        if (last) fetchActivity(last.created_at);
                      }}
                      disabled={activityLoading}
                      style={{ marginTop: 12, padding: '8px 16px', borderRadius: 6, border: '1px solid var(--color-border-soft, #ECECEF)', background: 'none', color: 'var(--color-text-muted)', fontSize: 13, cursor: 'pointer', width: '100%' }}
                    >
                      {activityLoading ? 'Loading...' : 'Load more'}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Other Workspaces — joined workspaces on top, pending invites below */
            <>
              {invitesLoading ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading...</p>
              ) : (
                <>
                  {/* Workspaces I'm In (accepted invites) */}
                  {(() => {
                    const joined = sortedInvites.filter(i => i.status === 'accepted');
                    return joined.length > 0 ? (
                      <div style={{ marginBottom: 24 }}>
                        <h3 style={{ marginBottom: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faint)', fontWeight: 600 }}>Workspaces I'm In</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {joined.map((invite) => (
                          <div key={invite.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 8, background: 'var(--color-surface-hover)' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--color-text-primary)' }}>{invite.containerName || invite.ownerName || invite.ownerEmail.split('@')[0]}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{invite.ownerEmail} · {invite.permission === 'read_write' ? 'Read & Write' : 'Read Only'}</div>
                            </div>
                            <button
                              onClick={() => handleInviteToggle(invite, false)}
                              style={{ padding: '5px 12px', fontSize: 12, borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              Leave
                            </button>
                          </div>
                        ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Pending Invites */}
                  {(() => {
                    const pending = sortedInvites.filter(i => i.status === 'pending');
                    const ignored = sortedInvites.filter(i => i.status === 'ignored');
                    return (pending.length > 0 || ignored.length > 0) ? (
                      <div>
                        <h3 style={{ marginBottom: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faint)', fontWeight: 600 }}>
                          Pending Invites{pending.length > 0 ? ` (${pending.length})` : ''}
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[...pending, ...ignored].map((invite) => (
                          <div key={invite.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 8, background: 'var(--color-surface-hover)', opacity: invite.status === 'ignored' ? 0.5 : 1 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--color-text-primary)' }}>{invite.containerName || invite.ownerName || invite.ownerEmail.split('@')[0]}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{invite.ownerEmail}</div>
                            </div>
                            {invite.status === 'ignored' ? (
                              <button
                                onClick={() => handleInviteToggle(invite, true)}
                                style={{ padding: '5px 12px', fontSize: 12, borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                Rejoin
                              </button>
                            ) : (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button
                                  onClick={() => handleInviteToggle(invite, true)}
                                  style={{ padding: '5px 12px', fontSize: 12, borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleInviteToggle(invite, false)}
                                  style={{ padding: '5px 12px', fontSize: 12, borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                  Ignore
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Empty state */}
                  {sortedInvites.length === 0 && (
                    <>
                      <div style={{ margin: '0', borderTop: '1px solid var(--color-border, #DADCE0)' }} />
                      <div style={{ textAlign: 'center', padding: '24px 0' }}>
                        <p style={{ fontSize: 14, color: 'var(--color-text-faint)', margin: 0 }}>No other workspaces yet</p>
                        <p style={{ fontSize: 13, color: 'var(--color-text-faint)', margin: '4px 0 0', opacity: 0.7 }}>When you&rsquo;re invited to join another workspace by its owner, it will show here.</p>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const portalRoot = document.getElementById('modal-portal-root') ?? document.body;

  const removeConfirmModal = removeConfirmation ? (
    <RemoveConfirmModal
      email={removeConfirmation.email}
      portalRoot={portalRoot}
      modalRef={removeModalRef}
      onOverlayMouseDown={handleRemoveOverlayMouseDown}
      onOverlayClick={handleRemoveOverlayClick}
      onCancel={() => setRemoveConfirmation(null)}
      onConfirm={executeRemoveMember}
    />
  ) : null;

  const blockConfirmModal = blockConfirmation ? (
    <BlockConfirmModal
      portalRoot={portalRoot}
      modalRef={blockModalRef}
      onOverlayMouseDown={handleBlockOverlayMouseDown}
      onOverlayClick={handleBlockOverlayClick}
      onCancel={() => setBlockConfirmation(null)}
      onConfirm={executeRejectInvite}
    />
  ) : null;

  return (
    <>
      {createPortal(modalContent, document.body)}
      {removeConfirmModal}
      {blockConfirmModal}
    </>
  );
}
